<template>
  <div>
    <OnexAlert data-comp-id="alert-message-info" :type="Notifications.info" :dismissible="false">
      <template #default>
        <p>{{ $t('L_please_enter_your_contact_information_this_information_will_be') }}</p>
      </template>
    </OnexAlert>
  </div>
  <v-container class="mt-4">
    <v-row class="mt-4 flex-column">
      <v-form
        ref="submit_reinstatement_step_4"
        id="submit_reinstatement_step_4"
        v-model="validForm"
        autocomplete="off"
      >
        <v-row>
          <v-col cols="12" class="font-size--16 mb-4 font-bold">
            {{ $t('L_enter_the_primary_insured_information') }}
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <div class="font-size--12 font-weight-bold">
              <p>{{ $t('L_first_name_email_address', { name: primaryName }) }}</p>
            </div>
            <div class="font-size--16">
              {{ primaryEmailAddress }}
            </div>
            <div v-if="isPhoneVerified" class="mt-4 mb-4">
              <div class="font-size--12 font-weight-bold">
                <p>{{ $t('L_first_name_mobile_phone', { name: primaryName }) }}</p>
              </div>
              <div class="font-size--16">
                {{ phoneFormat(primaryMobilePhone) }}
              </div>
            </div>
            <OnexPhone
              v-else
              class="mt-4"
              v-model="primaryMobilePhone"
              :label="$t('L_first_name_mobile_phone', { name: primaryName })"
              required
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6" md="4" class="font-size--16" v-if="hasSpouse">
            <div class="font-size--16 mb-4 font-weight-bold">
              {{ $t('L_enter_the_spouse_insured_information') }}
            </div>
            <div class="font-size--16 mb-4 font-weight-regular">
              {{
                $t(
                  'L_since_a_spouse_insured_is_included_on_this_policy_please_provide_their_email_and_mobile_phone_number'
                )
              }}
            </div>
            <OnexEmail
              v-model="spouseEmailAddress"
              :label="$t('L_spouse_first_name_email', { name: spouseName })"
              required
            />
            <OnexEmail
              v-model="spouseVerifyEmailAddress"
              :label="$t('L_verify_spouse_first_name_email', { name: spouseName })"
              :validation-messages="{ notMatchingEmail: $t('L_confirm_email_match') }"
              required
            />
            <OnexPhone
              v-model="spouseMobilePhone"
              :label="$t('L_spouse_first_name_mobile_phone', { name: spouseName })"
              required
            />
          </v-col>
        </v-row>

        <!-- Button -->
        <v-row class="d-flex align-center justify-center py-4">
          <v-col cols="12" sm="6" md="4" class="pr-4">
            <OnexButton
              :disabled="disableActions"
              block
              variant="outlined"
              color="primary"
              @click="backAction"
            >
              {{ $t('L_back') }}
            </OnexButton>
          </v-col>
          <v-col cols="12" sm="6" md="4" class="pr-4">
            <OnexButton
              :disabled="disableActions || !validForm || !isFormDataValid"
              target="_blank"
              block
              color="primary"
              @click="continueAction"
            >
              {{ $t('L_continue') }}
            </OnexButton>
          </v-col>
        </v-row>
      </v-form>
    </v-row>
  </v-container>
</template>

<script lang="ts">
import { mapState } from 'pinia';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { defineComponent, type ComponentOptions } from 'vue';
import { OnexButton, OnexEmail, OnexPhone, OnexAlert, Notifications } from '@primerica/onex-ui-lib';

const componentOptions: ComponentOptions = {
  name: 'ReinstatementProcessInformation',
  components: {
    OnexAlert,
    OnexEmail,
    OnexPhone,
    OnexButton,
  },
  props: {
    disableActions: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    primaryName: '',
    spouseName: '',
    primaryEmailAddress: '',
    primaryMobilePhone: '',
    spouseEmailAddress: '',
    spouseVerifyEmailAddress: '',
    spouseMobilePhone: '',
    validForm: true,
    Notifications,
  }),
  computed: {
    ...mapState(usePolicyStore, {
      currentPolicy: 'currentPolicy',
    }),
    ...mapState(useCustomerStore, ['customerProfile']),
    isPhoneVerified() {
      return this.customerProfile?.phoneVerified;
    },
    hasSpouse() {
      return !!this.spouseName;
    },
    isFormDataValid() {
      if (this.primaryMobilePhone.length < 10) {
        return false;
      }
      if (this.hasSpouse) {
        if (this.spouseMobilePhone.length < 10) {
          return false;
        }
        if (this.spouseEmailAddress !== this.spouseVerifyEmailAddress) {
          return false;
        }
        if (this.spouseEmailAddress.trim() === '') {
          return false;
        }
      }
      return true;
    },
  },
  methods: {
    phoneFormat(number: string) {
      return number.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
    },
    continueAction() {
      const userObj = {
        primaryName: this.primaryName,
        spouseName: this.spouseName,
        primaryEmailAddress: this.primaryEmailAddress,
        primaryMobilePhone: this.primaryMobilePhone,
        spouseEmailAddress: this.spouseEmailAddress,
        spouseVerifyEmailAddress: this.spouseVerifyEmailAddress,
        spouseMobilePhone: this.spouseMobilePhone,
      };

      // alert(JSON.stringify(userObj, 1, 3));
      this.$emit('continue-to-the-next-reinstatement-step', userObj);
    },
    backAction() {
      this.$emit('backClicked');
    },
  },
  mounted() {
    this.primaryName = this.customerProfile?.firstName;
    this.primaryEmailAddress = this.customerProfile?.email;
    this.spouseName = this.currentPolicy?.data?.spouseFirstName;
    if (this.customerProfile?.phoneVerified) {
      this.primaryMobilePhone = this.customerProfile?.mobilePhone;
    }
  },
};

export default defineComponent(componentOptions);
</script>
